import { useRef, useState, useEffect, useLayoutEffect, useContext, useMemo, Dispatch, SetStateAction } from "react";
import readNDJSONStream from "ndjson-readablestream";
import { throttle } from "lodash";
import { Spinner, ISpinnerStyles } from "@fluentui/react";
import { v4 as uuid } from "uuid";

import {
    chatApi,
    ChatRequest,
    generateHistory,
    HistoryRequest,
    AssistantsLoadingState,
    ChatMessage,
    ChatResponse,
    Message,
    MessageContext,
    MessageFile,
    getDocumentText,
    getFreshCitationUrl,
    readErrorMessage,
    AuthRequiredError
} from "../../api";
import { Answer, AnswerError, AnswerLoading } from "../../components/Answer";
import { QuestionInput, QuestionInputHandle } from "../../components/QuestionInput";
import { FormInput } from "../../components/FormInput";
import { UserChatMessage } from "../../components/UserChatMessage";
import { ClearChatProvider } from "../../components/ClearChatButton/ClearChatContext";
import { getActiveMsalInstance } from "../../authConfig";
import { AppStateContext } from "../../state/AppProvider";
import { isUsableChatFile } from "../../state/AppState";

import { EmailDialog } from "../../components/EmailDialog";
import { MobileSidebar } from "../../components/MobileSidebar";
import { DefaultSidebar } from "../../components/DefaultSidebar";
import { ChatHeader } from "../../components/ChatHeader";
import { ButtonTooltip } from "../../components/ButtonTooltip";

import { ChatHistoryLoadingState, CosmosDBStatus } from "../../api";
import { Conversation } from "../../api";
import { LoginExpired } from "../../components/LoginExpired";
import { replaceTemplatePlaceholders } from "../../utils/templateUtils"
import { DOCUMENT_TYPES_LABEL, citationFileName, isBrowserRenderable, isSupportedDocument, legacyOfficeMessage } from "../../constants/fileUpload";

type ConversationView = {
    answers: [user: string, response: ChatResponse][];
    questionFiles: { files: string[] }[];
    historyFiles: { name: string; text: string }[];
};

// answers and questionFiles must stay index-aligned: the chat render reads questionFiles[index] for every answer.
const buildConversationView = (messages: ChatMessage[]): ConversationView => {
    const answers: [user: string, response: ChatResponse][] = [];
    const questionFiles: { files: string[] }[] = [];
    const historyFiles: { name: string; text: string }[] = [];
    for (let i = 0; i < messages.length - 1; i++) {
        if (messages[i].message.role !== "user" || messages[i + 1].message.role !== "assistant") {
            continue;
        }
        answers.push([
            messages[i].message.content,
            {
                choices: [
                    {
                        index: i,
                        message: messages[i + 1].message,
                        context: messages[i + 1].context,
                        session_state: null
                    }
                ]
            }
        ]);
        const newFiles = messages[i].context?.metadata?.files?.filter(fileItem => fileItem.newFile) ?? [];
        questionFiles.push({ files: newFiles.map(fileItem => fileItem.fileName) });
        historyFiles.push(...newFiles.map(fileItem => ({ name: fileItem.fileName, text: fileItem.fileText })));
    }
    return { answers, questionFiles, historyFiles };
};

const Chat = () => {
    let userErrorMessage: string = "Something went wrong. If this issue persists, please contact helpdesk@jtcgroup.com.";
    const appStateContext = useContext(AppStateContext);

    const isScreeningUrl = window.location.pathname === '/screening'
    const [iframeKey, setIframeKey] = useState(0);

    const [conversationId, setConversationId] = useState<string>();
    const lastQuestionRef = useRef<string>("");
    const lastQuestionFilesRef = useRef<string[]>([]);
    const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);
    const chatScrollRef = useRef<HTMLDivElement | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);
    const questionInputRef = useRef<QuestionInputHandle>(null);

    const [reasoning, setReasoning] = useState<boolean>(false);
    const [webSearch, setWebSearch] = useState<boolean>(false);
    const [streamingReasoning, setStreamingReasoning] = useState<string>("");
    const [streamingWebSearch, setStreamingWebSearch] = useState<string>("");
    const [streamingFileGeneration, setStreamingFileGeneration] = useState<string>("");
    const tools = appStateContext?.state.assistant?.tools || [];
    const effectiveReasoning = reasoning && tools.includes("reasoning");
    const effectiveWebSearch = webSearch && tools.includes("web_search");

    // Every assistant switch resets the toggles to that assistant's
    // tool_defaults; assistants without the field get no defaults.
    useEffect(() => {
        const assistant = appStateContext?.state.assistant;
        if (!assistant) return;
        const defaults = assistant.tool_defaults ?? [];
        setReasoning(defaults.includes("reasoning"));
        setWebSearch(defaults.includes("web_search"));
    }, [appStateContext?.state.assistant?.id]);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isStreaming, setIsStreaming] = useState<boolean>(false);
    const [error, setError] = useState<unknown>();

    const [showEmailDialog, setShowEmailDialog] = useState<boolean>(false);
    const [activeCitation, setActiveCitation] = useState<string>();
    const [activeCitationTitle, setActiveCitationTitle] = useState<string>();
    const [showThoughts, setShowThoughts] = useState<boolean[]>([]);
    const [activeVideos, setActiveVideos] = useState<string[]>([]);
    const [questionFiles, setQuestionFiles] = useState<{ files: string[] }[]>([]);
    const [isAutoScroll, setIsAutoScroll] = useState<boolean>(true);

    const [answers, setAnswers] = useState<[user: string, response: ChatResponse][]>([]);
    const [streamedAnswers, setstreamedAnswers] = useState<[user: string, response: ChatResponse][]>([]);

    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleAsyncRequest = async (question: string, answers: [string, ChatResponse][], setAnswers: Dispatch<SetStateAction<[user: string, response: ChatResponse][]>>, responseBody: ReadableStream<Uint8Array>) => {
        let answer: string = "";
        let reasoningText: string = "";
        let askResponse: ChatResponse | undefined = undefined;

        const updateState = (newContent: string) => {
            return new Promise(resolve => {
                setTimeout(() => {
                    answer += newContent;
                    const latestResponse: ChatResponse = {
                        ...askResponse!,
                        choices: [{ ...askResponse!.choices[0], message: { content: answer, role: askResponse!.choices[0].message.role } }]
                    };
                    setstreamedAnswers([...answers, [question, latestResponse]]);
                    resolve(null);
                }, 33);
            });
        };
        try {
            setIsStreaming(true);
            for await (const event of readNDJSONStream(responseBody)) {
                if (
                    event["choices"] &&
                    event["choices"][0]["context"] &&
                    event["choices"][0]["context"]["metadata"] &&
                    event["choices"][0]["context"]["metadata"]["thoughts"]
                ) {
                    event["choices"][0]["message"] = event["choices"][0]["delta"];
                    askResponse = event as ChatResponse;
                } else if (event["choices"] && event["choices"][0]["delta"]["reasoning"]) {
                    const chunk = event["choices"][0]["delta"]["reasoning"];
                    reasoningText += chunk;
                    await new Promise(resolve => {
                        setTimeout(() => {
                            setStreamingReasoning(reasoningText);
                            setIsLoading(false);
                            if (askResponse?.choices?.[0]) {
                                const latestResponse: ChatResponse = {
                                    ...askResponse,
                                    choices: [{ ...askResponse.choices[0], message: { content: answer, role: askResponse.choices[0].message.role } }]
                                };
                                setstreamedAnswers([...answers, [question, latestResponse]]);
                            }
                            resolve(null);
                        }, 33);
                    });
                }
                else if (event["choices"] && event["choices"][0]["delta"]["web_search"]) {
                    const status = event["choices"][0]["delta"]["web_search"] as string;
                    await new Promise(resolve => {
                        setTimeout(() => {
                            setStreamingWebSearch(status);
                            setIsLoading(false);
                            // Push a placeholder streamed answer so the Answer component renders
                            if (askResponse?.choices?.[0]) {
                                const latestResponse: ChatResponse = {
                                    ...askResponse,
                                    choices: [{ ...askResponse.choices[0], message: { content: answer, role: askResponse.choices[0].message.role } }]
                                };
                                setstreamedAnswers([...answers, [question, latestResponse]]);
                            }
                            resolve(null);
                        }, 33);
                    });
                } else if (event["choices"] && event["choices"][0]["delta"]["file_generation"]) {
                    const status = event["choices"][0]["delta"]["file_generation"] as string;
                    await new Promise(resolve => {
                        setTimeout(() => {
                            setStreamingFileGeneration(status);
                            setIsLoading(false);
                            // Push a placeholder streamed answer so the Answer component renders
                            if (askResponse?.choices?.[0]) {
                                const latestResponse: ChatResponse = {
                                    ...askResponse,
                                    choices: [{ ...askResponse.choices[0], message: { content: answer, role: askResponse.choices[0].message.role } }]
                                };
                                setstreamedAnswers([...answers, [question, latestResponse]]);
                            }
                            resolve(null);
                        }, 33);
                    });
                } else if (event["choices"] && event["choices"][0]["delta"]["content"]) {
                    setIsLoading(false);
                    await updateState(event["choices"][0]["delta"]["content"]);
                } else if (event["choices"] && event["choices"][0]["context"]) {
                    askResponse!.choices[0].context = { ...askResponse!.choices[0].context, ...event["choices"][0]["context"] };
                } else if (event["error"]) {
                    throw Error(event["error"]);
                }
            }
        } catch (e: any) {
            // User pressed Stop: keep the partial answer if a first response chunk
            // arrived; otherwise rethrow so the caller cancels the turn cleanly.
            // No guard is needed on the 33ms ticks above: each one is awaited by
            // the loop, so none can fire after this point.
            if (e?.name !== "AbortError" || !askResponse) {
                throw e;
            }
            askResponse.choices[0].context = { ...askResponse.choices[0].context, stopped: true };
        } finally {
            setIsStreaming(false);
            setStreamingReasoning("");
            setStreamingWebSearch("");
            setStreamingFileGeneration("");
        }

        if (reasoningText && askResponse?.choices?.[0]?.context?.metadata) {
            askResponse.choices[0].context.metadata.reasoning_summary = reasoningText;
        }

        const fullResponse: ChatResponse = {
            ...askResponse!,
            choices: [{ ...askResponse!.choices[0], message: { content: answer, role: askResponse!.choices[0].message.role } }]
        };
        return fullResponse;
    };

    const makeApiRequest = async (question: string) => {
        setStreamingReasoning("");
        setStreamingWebSearch("");
        setStreamingFileGeneration("");
        // Captured so an early stop (no answer content yet) can undo this turn.
        const previousQuestion = lastQuestionRef.current;
        const previousQuestionFiles = lastQuestionFilesRef.current;
        const previousError = error;
        lastQuestionRef.current = question;
        // Failed uploads never reach the model; purge them first so the indices below
        // line up with the reducer's array after this dispatch.
        appStateContext?.dispatch({ type: "REMOVE_FAILED_FILES" });
        const usableFiles = (appStateContext?.state.files ?? []).filter(isUsableChatFile);
        const newQuestionFiles = usableFiles.filter(fileItem => fileItem.new).map(fileItem => fileItem.file.name);
        lastQuestionFilesRef.current = newQuestionFiles;
        // Index-based so an early-stop rollback restores exactly the entries this
        // send consumed, not same-named copies from history or earlier turns.
        const newQuestionFileIndices = usableFiles.flatMap((fileItem, index) => (fileItem.new ? [index] : []));

        error && setError(undefined);
        setIsLoading(true);
        setActiveCitation(undefined);
        setActiveCitationTitle(undefined);
        setShowThoughts(currentShowThoughts => [...currentShowThoughts, false]);
        setActiveVideos(currentActiveVideos => [...currentActiveVideos, ""]);
        setQuestionFiles(currentQuestionFiles => [...currentQuestionFiles, { files: newQuestionFiles }]);
        appStateContext?.dispatch({ type: "TOGGLE_ANSWER_LOADING" });
        appStateContext?.dispatch({ type: "UPDATE_ALL_FILES_NEW" });

        // Created before any await so Stop works even during token acquisition.
        const controller = new AbortController();
        abortControllerRef.current = controller;

        try {
            const messages: Message[] = answers.flatMap(a => [
                { content: a[0], role: "user" },
                { content: a[1].choices[0].message.content, role: "assistant" }
            ]);

            const attachedFiles: MessageFile[] = usableFiles.map(fileItem => ({
                fileName: fileItem.file.name,
                fileText: fileItem.text,
                newFile: fileItem.new
            }));
            const buildUserContext = (files: MessageFile[]): MessageContext => ({
                chat_status: answers.length ? answers[answers.length - 1][1].choices[0].context.chat_status : null,
                metadata: files.length > 0 ? { files } : undefined,
                assistant_id: appStateContext?.state.assistant?.id,
                assistant_prompt_message: appStateContext?.state.assistant?.system_prompt_message,
                assistant_config: appStateContext?.state.assistant?.config
            });
            // The model needs every attachment on every turn. History only needs this
            // turn's: earlier ones are already stored with the turn they were uploaded on.
            const chatContext = buildUserContext(attachedFiles);
            const storedContext = buildUserContext(attachedFiles.filter(file => file.newFile));

            const request: ChatRequest = {
                assistant: appStateContext?.state.assistant?.assistant || "",
                messages: [...messages, { content: question, role: "user" }],
                context: chatContext,
                stream: true,
                // ChatAppProtocol: Client must pass on any session state received from the server
                session_state: answers.length ? answers[answers.length - 1][1].choices[0].session_state : null,
                reasoning: effectiveReasoning,
                web_search: effectiveWebSearch,
            };

            const response = await chatApi(request, controller.signal);
            if (!response.ok) {
                throw Error(await readErrorMessage(response, `The request failed with status ${response.status}.`));
            }
            if (!response.body) {
                throw Error("No response body");
            }
            const parsedResponse: ChatResponse = await handleAsyncRequest(question, answers, setAnswers, response.body);
            if (parsedResponse.choices[0].message.content === "") {
                throw Error(userErrorMessage);
            }

            setAnswers((prevAnswers: [user: string, response: ChatResponse][]) => {
                const updatedAnswers: [user: string, response: ChatResponse][] = [...prevAnswers, [question, parsedResponse]];

                const messages: Message[] = updatedAnswers.flatMap(a => [
                    { content: a[0], role: "user" },
                    { content: a[1].choices[0].message.content, role: "assistant" }
                ]);

                const historyRequest: HistoryRequest = {
                    conversation_id: conversationId,
                    assistant: appStateContext?.state.assistant?.assistant || "",
                    assistant_id: appStateContext?.state.assistant?.id || "",
                    messages: [...messages],
                    context: parsedResponse.choices[0].context,
                    user_context: storedContext
                };

                generateHistory(historyRequest)
                    .then(histResponse => {
                        // Ensure the response is OK and parse it as JSON
                        if (!histResponse.ok) {
                            throw Error("Network response was not ok");
                        }
                        return histResponse.json();
                    })
                    .then(data => {
                        let resultConversation: Conversation | undefined;
                        if (!data.conversation) {
                            throw Error("Invalid response body");
                        }
                        if (!conversationId) {
                            resultConversation = {
                                id: data.conversation.id,
                                assistant: data.conversation.assistant,
                                assistant_id: data.conversation.assistant_id,
                                title: data.conversation.title,
                                messages: data.messages,
                                date: data.conversation.date
                            };
                            appStateContext?.dispatch({ type: "UPDATE_CURRENT_CHAT", payload: resultConversation });
                            setConversationId(data.conversation.id);
                        } else {
                            resultConversation = appStateContext?.state?.chatHistory?.find(conv => conv.id === conversationId);
                            if (!resultConversation) {
                                throw Error("Invalid conversation id");
                            }
                            resultConversation.messages.push(data.messages[0]);
                            resultConversation.messages.push(data.messages[1]);
                            resultConversation.date = data.conversation.date;
                            let updatedConversation = { ...resultConversation };
                            appStateContext?.dispatch({ type: "UPDATE_CURRENT_CHAT", payload: resultConversation });
                        }
                        return resultConversation;
                    })
                    .then(resultConversation => {
                        if (resultConversation) {
                            appStateContext?.dispatch({ type: "UPDATE_CHAT_HISTORY", payload: resultConversation });
                        }
                    })
                    .catch(e => {
                        // Session expiry is surfaced by the session-expired modal; don't
                        // misreport it as a CosmosDB outage.
                        if (e instanceof AuthRequiredError) {
                            console.error(e);
                            return;
                        }
                        appStateContext?.dispatch({ type: "UPDATE_CHAT_HISTORY_LOADING_STATE", payload: ChatHistoryLoadingState.Fail });
                        appStateContext?.dispatch({ type: "SET_COSMOSDB_STATUS", payload: { cosmosDB: false, status: CosmosDBStatus.NotWorking } });
                        console.error(e);
                    });

                return updatedAnswers;
            });
        } catch (e) {
            if (controller.signal.aborted) {
                // User stopped before any answer content arrived: undo this turn's
                // optimistic state and put the question back in the composer.
                lastQuestionRef.current = previousQuestion;
                lastQuestionFilesRef.current = previousQuestionFiles;
                setError(previousError);
                // A status tick may have pushed this turn's placeholder; without a
                // reset it ghosts back on the next send (isStreaming renders
                // streamedAnswers before the first chunk arrives).
                setstreamedAnswers(answers);
                // The reload effect can rewrite these arrays mid-flight (first-save
                // race), dropping this turn's entry already — only roll back an
                // entry that is still there.
                setShowThoughts(prev => (prev.length > answers.length ? prev.slice(0, -1) : prev));
                setActiveVideos(prev => (prev.length > answers.length ? prev.slice(0, -1) : prev));
                setQuestionFiles(prev => (prev.length > answers.length ? prev.slice(0, -1) : prev));
                if (newQuestionFileIndices.length) {
                    appStateContext?.dispatch({ type: "MARK_FILES_NEW", payload: newQuestionFileIndices });
                }
                questionInputRef.current?.restoreQuestion(question);
            } else {
                // AuthRequiredError renders alongside the session-expired modal; show its
                // message without the error-class prefix so Retry reads naturally after re-auth.
                setError(e instanceof AuthRequiredError ? e.message : e);
            }
        } finally {
            setIsLoading(false);
            appStateContext?.dispatch({ type: "TOGGLE_ANSWER_LOADING" });
            abortControllerRef.current = null;
        }
    };

    // All stop finalization (state resets, partial-answer commit, toggle pairing)
    // flows through makeApiRequest's existing await chain; abort() is idempotent.
    const stopGenerating = () => {
        abortControllerRef.current?.abort();
    };

    const clearChat = () => {
        if ((lastQuestionRef.current || appStateContext?.state.files.length) && !appStateContext?.state.answerLoading) {
            lastQuestionRef.current = "";
            lastQuestionFilesRef.current = [];
            error && setError(undefined);
            setActiveCitation(undefined);
            setActiveCitationTitle(undefined);
            setShowThoughts([]);
            setActiveVideos([]);
            setAnswers([]);
            setstreamedAnswers([]);
            setQuestionFiles([]);
            setConversationId(undefined);
            appStateContext?.dispatch({ type: "REMOVE_ALL_FILES" });
            if (fileInputRef && fileInputRef.current) {
                fileInputRef.current.value = "";
            }
            appStateContext?.dispatch({ type: "UPDATE_CURRENT_CHAT", payload: null });
            if (windowWidth < 768 && appStateContext?.state.isChatHistoryOpen) {
                appStateContext?.dispatch({ type: "TOGGLE_CHAT_HISTORY" });
            }
        }
    };

    useEffect(() => {
        chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" });
    }, [isLoading]);

    useEffect(() => {
        if (isAutoScroll) {
            chatMessageStreamEnd.current?.scrollIntoView({ behavior: "auto" });
        }
    }, [streamedAnswers]);

    const scrollToEnd = () => {
        chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" });
    };

    const handleScroll = () => {
        const chatContainer = chatScrollRef.current;
        if (chatContainer) {
            const { scrollTop, scrollHeight, clientHeight } = chatContainer;
            const isScrolledToBottom = Math.ceil(scrollTop + clientHeight) + 1 >= scrollHeight;
            setIsAutoScroll(isScrolledToBottom);
        }
    };

    useEffect(() => {
        const chatContainer = chatScrollRef.current;
        if (chatContainer) {
            chatContainer.addEventListener("scroll", handleScroll);
        }
        return () => {
            if (chatContainer) {
                chatContainer.removeEventListener("scroll", handleScroll);
            }
        };
    }, [lastQuestionRef.current]);

    const onShowCitation = async (citation: string, title: string) => {
        if (windowWidth < 1280) {
            // Open the tab synchronously so popup blockers don't eat it, then
            // navigate it once the freshly tokenised URL is ready.
            const newTab = window.open("", "_blank");
            try {
                const path = await getFreshCitationUrl(citation, appStateContext?.state.assistant?.type || "JTC", appStateContext?.state.assistant?.id);
                if (newTab) {
                    newTab.location.href = path;
                }
            } catch (e) {
                newTab?.close();
                if (!(e instanceof AuthRequiredError)) {
                    console.error(e);
                }
            }
        } else {
            try {
                const path = await getFreshCitationUrl(citation, appStateContext?.state.assistant?.type || "JTC", appStateContext?.state.assistant?.id);
                setActiveCitation(path);
                setActiveCitationTitle(title);
                setIframeKey(prevKey => prevKey + 1);
            } catch (e) {
                if (!(e instanceof AuthRequiredError)) {
                    console.error(e);
                }
            }
        }
    };

    const onCloseCitation = () => {
        setActiveCitation(undefined);
        setActiveCitationTitle(undefined);
    };

    const onOpenCitation = () => {
        window.open(activeCitation, "_blank");
    };

    const onThoughtProcessClicked = (index: number) => {
        if (appStateContext?.state.currentChat && showThoughts.length === 0) {
            const updatedShowThoughts: boolean[] = [];
            const messages = appStateContext.state.currentChat.messages;

            for (let i = 0; i < messages.length - 1; i++) {
                if (messages[i].message.role === "user" && messages[i + 1].message.role === "assistant") {
                    updatedShowThoughts.push(false);
                }
            }
            setShowThoughts(updatedShowThoughts);
        }

        setShowThoughts(currentShowThoughts => {
            const updatedShowThoughts = [...currentShowThoughts];
            updatedShowThoughts[index] = !updatedShowThoughts[index];
            return updatedShowThoughts;
        });
    };

    const handleHistoryClick = () => {
        appStateContext?.state.isChatHistoryOpen
            ? document.documentElement.style.setProperty("--ch", `${0}px`)
            : document.documentElement.style.setProperty("--ch", `${300}px`);
        appStateContext?.dispatch({ type: "TOGGLE_CHAT_HISTORY" });
    };

    const [windowHeight, setWindowHeight] = useState(window.innerHeight);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    useEffect(() => {
        const vh = window.innerHeight;
        document.documentElement.style.setProperty("--vh", `${vh}px`);
        document.documentElement.style.setProperty("--ch", `${0}px`);
    }, []);

    useEffect(() => {
        // The function won't be called more often than every 200ms
        const handleResize = throttle(() => {
            const vh = window.innerHeight;
            const vw = window.innerWidth;
            setWindowHeight(vh);
            setWindowWidth(vw);
            document.documentElement.style.setProperty("--vh", `${vh}px`);
        }, 200);

        window.addEventListener("resize", handleResize);

        // Clean up the event listener on component unmount
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    useEffect(() => {
        if (appStateContext?.state.currentChat && appStateContext?.state.currentChat.id !== conversationId) {
            if (conversationId) {
                appStateContext?.dispatch({ type: "REMOVE_ALL_FILES" });
                if (fileInputRef && fileInputRef.current) {
                    fileInputRef.current.value = "";
                }
            }
            error && setError(undefined);
            setActiveCitation(undefined);

            const messages = appStateContext.state.currentChat.messages;
            const view = buildConversationView(messages);

            view.historyFiles.forEach(file => appStateContext?.dispatch({ type: "ADD_CHAT_HISTORY_FILE", payload: file }));
            if (messages.length && messages[messages.length - 1].message.role === "user") {
                // Final assistant message is missing
                setError(userErrorMessage);
            }

            setShowThoughts(view.answers.map(() => false));
            setActiveVideos([]);
            setAnswers(view.answers);
            setstreamedAnswers(view.answers);
            setQuestionFiles(view.questionFiles);

            setConversationId(appStateContext.state.currentChat.id);

            if (appStateContext.state.assistants) {
                const currentAssistant = appStateContext.state.assistants?.find(assist =>
                    assist.assistant === 'custom'
                        ? assist.id === appStateContext.state.currentChat?.assistant_id
                        : assist.assistant === appStateContext.state.currentChat?.assistant
                );
                appStateContext?.dispatch({ type: "UPDATE_ASSISTANT", payload: currentAssistant || null });
            }

            for (let i = messages.length - 1; i >= 0; i--) {
                if (messages[i].message.role === "user") {
                    lastQuestionRef.current = messages[i].message.content as string;
                    break;
                }
            }
        } else if (appStateContext?.state.currentChat) {
            // Same conversation reloaded: global file state is already current, so historyFiles are not re-dispatched.
            const view = buildConversationView(appStateContext.state.currentChat.messages);
            setAnswers(view.answers);
            setstreamedAnswers(view.answers);
            setQuestionFiles(view.questionFiles);
            setShowThoughts(prev => view.answers.map((_, i) => prev[i] ?? false));
        } else {
            clearChat();
        }
    }, [appStateContext?.state.currentChat]);

    function copyToClipboard(text: string) {
        if (navigator.clipboard) {
            navigator.clipboard
                .writeText(text)
                .then(() => {})
                .catch(err => {
                    console.error("Failed to copy text using Clipboard API: ", err);
                    // Try the fallback method
                    fallbackCopyTextToClipboard(text);
                });
        } else {
            fallbackCopyTextToClipboard(text);
        }
    }

    function fallbackCopyTextToClipboard(text: string) {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
            // Using the Clipboard API again for the fallback method
            navigator.clipboard.writeText(text);
        } catch (err) {
            console.error("Fallback failed too: ", err);
        }
        document.body.removeChild(textArea);
    }

    const spinnerStyles: ISpinnerStyles = {
        circle: {
            borderColor: "#019dcd #019dcd #c2f1ff00 #c2f1ff00",
            height: "16px",
            width: "16px",
            borderWidth: "2px",
            borderStyle: "solid"
        }
    };

    const uploadFile = async () => {
        const input = fileInputRef.current;
        const limits = appStateContext?.state.chatUploadLimits;
        // Limits are fetched at startup; attachments wait for them.
        if (!input || !input.files?.length || !appStateContext || !limits) {
            return;
        }
        const filesArray = Array.from(input.files);
        const legacyMessage = filesArray.map(file => legacyOfficeMessage(file.name)).find(message => message !== null);
        if (legacyMessage) {
            alert(legacyMessage);
            return;
        }
        if (!filesArray.every(file => isSupportedDocument(file.name))) {
            alert(`Only ${DOCUMENT_TYPES_LABEL} files are allowed.`);
            return;
        }

        const attachedFiles = appStateContext.state.files.filter(fileItem => fileItem.new && fileItem.status !== "Error");
        // Extraction results arrive one at a time, so this running total also covers files picked together.
        let attachedTokens = attachedFiles.reduce((total, fileItem) => total + (fileItem.tokenCount ?? 0), 0);

        const extractFileText = (file: File) => {
            appStateContext.dispatch({ type: "SET_ANSWER_LOADING", payload: true });
            const formData = new FormData();
            formData.append("file", file);
            getDocumentText(formData)
                .then(async response => {
                    if (!response.ok) {
                        throw new Error(await readErrorMessage(response, "Unable to upload"));
                    }
                    return response.json();
                })
                .then((data: { text: string; token_count: number }) => {
                    if (attachedTokens + data.token_count > limits.max_total_file_tokens) {
                        appStateContext.dispatch({
                            type: "SET_FILE_ERROR",
                            payload: {
                                name: file.name,
                                error: `Would exceed the ${limits.max_total_file_tokens.toLocaleString()}-token limit for one message.`
                            }
                        });
                        return;
                    }
                    attachedTokens += data.token_count;
                    appStateContext.dispatch({
                        type: "SET_FILE_RESULT",
                        payload: { name: file.name, text: data.text, tokenCount: data.token_count }
                    });
                })
                .catch((error: unknown) => {
                    console.error(error);
                    appStateContext.dispatch({
                        type: "SET_FILE_ERROR",
                        payload: { name: file.name, error: error instanceof Error ? error.message : "Unable to upload" }
                    });
                })
                .finally(() => appStateContext.dispatch({ type: "SET_ANSWER_LOADING", payload: false }));
        };

        filesArray.forEach((file, position) => {
            appStateContext.dispatch({ type: "ADD_FILE", payload: file.name });
            if (attachedFiles.length + position >= limits.max_files) {
                appStateContext.dispatch({
                    type: "SET_FILE_ERROR",
                    payload: { name: file.name, error: `Only ${limits.max_files} files can be attached to one message.` }
                });
                return;
            }
            if (file.size > limits.max_file_bytes) {
                appStateContext.dispatch({
                    type: "SET_FILE_ERROR",
                    payload: { name: file.name, error: `Larger than the ${limits.max_file_bytes / (1024 * 1024)} MB limit.` }
                });
                return;
            }
            extractFileText(file);
        });
    };

    const onShowVideo = (citation: string, index: number) => {
        if (windowWidth < 1280) {
            window.open(citation, "_blank");
        } else {
            setActiveVideos(currentActiveVideos => {
                const updatedActiveVideos = [...currentActiveVideos];
                updatedActiveVideos[index] = citation;
                return updatedActiveVideos;
            });
        }
    };

    const onCloseVideo = (index: number) => {
        setActiveVideos(currentActiveVideos => {
            const updatedActiveVideos = [...currentActiveVideos];
            updatedActiveVideos[index] = "";
            return updatedActiveVideos;
        });
    };

    const instance = getActiveMsalInstance();
    const activeAccount = instance ? instance.getActiveAccount() : null;
    const firstName =
        activeAccount?.name?.trim().split(/\s+/)[0] || "there";

    const getGreetingResponse = (): ChatResponse | null => {
        if (!appStateContext?.state.assistant?.welcome_message) {
            return null;
        }

        // Format assistant files as a markdown list
        const assistantFiles = appStateContext.state.assistant.files?.length
            ? '\n' + appStateContext.state.assistant.files
                .map(file => `- ${file.fileName}`)
                .join('\n') + '\n'
            : 'No files available';

        const welcomeMessage = replaceTemplatePlaceholders(
            appStateContext.state.assistant.welcome_message,
            {
                firstName: firstName,
                assistantFiles: assistantFiles,
            }
        );

        return {
            choices: [
                {
                    index: 0,
                    message: {
                        role: "assistant",
                        content: welcomeMessage
                    },
                    context: {
                        chat_status: null,
                        metadata: {
                            thoughts: "None"
                        }
                    },
                    session_state: null
                }
            ]
        };
    };
    const isWorldCheck = appStateContext?.state.assistant?.assistant === "wc";
    const hasWelcomeMessage = !!appStateContext?.state.assistant?.welcome_message;
    const [showGreetingLoader, setShowGreetingLoader] = useState(false);

    // Add the welcome_message as a dependency so it recalculates when loaded
    const greetingResponse = useMemo<ChatResponse | null>(
        () => getGreetingResponse(),
        [appStateContext?.state.assistant?.welcome_message, firstName]
    );

    useLayoutEffect(() => {
        if (!hasWelcomeMessage || lastQuestionRef.current) return;
        setShowGreetingLoader(true);
    }, [appStateContext?.state.assistant?.id, hasWelcomeMessage]);

    useEffect(() => {
        if (!showGreetingLoader) return;
        const t = setTimeout(() => setShowGreetingLoader(false), 1_000);
        return () => clearTimeout(t);
    }, [showGreetingLoader]);


    return (
        <ClearChatProvider clearChat={clearChat}>
            <div className="relative z-0 flex h-full w-full overflow-hidden">
                {windowWidth >= 976 && (
                    <DefaultSidebar
                        onNewClick={clearChat}
                        newDisabled={(!lastQuestionRef.current && !appStateContext?.state.files.length) || appStateContext?.state.answerLoading}
                        onHistoryClick={handleHistoryClick}
                    />
                )}
                {appStateContext?.state.loginExpired && <LoginExpired open={true} />}
                <EmailDialog open={showEmailDialog} setOpen={b => setShowEmailDialog(b)} />
                <div
                    className={
                        "relative flex h-full max-w-full flex-1 flex-col overflow-hidden " +
                        (isScreeningUrl ? "worldcheck-background " : "login-background ") +
                        "transition-all duration-1000" +
                        (!lastQuestionRef.current ? "" : isScreeningUrl ? " worldcheck-background-chat" : " login-background-chat")
                    }
                >
                    <ChatHeader
                        onNewClick={clearChat}
                        newDisabled={(!lastQuestionRef.current && !appStateContext?.state.files.length) || appStateContext?.state.answerLoading}
                        onHistoryClick={handleHistoryClick}
                        showEmailDialog={showEmailDialog}
                        setShowEmailDialog={b => setShowEmailDialog(b)}
                        emptyChat={!lastQuestionRef.current}
                        windowWidth={windowWidth}
                    />
                    <main
                        className={
                            "relative h-full w-full flex-1 overflow-auto transition-width " +
                            (activeCitation ? "xl:flex " : "") +
                            (activeCitation && !appStateContext?.state.isChatHistoryOpen ? "xl:pl-12" : "")
                        }
                    >
                        <div role="presentation" tabIndex={0} className={"flex h-full flex-col focus-visible:outline-0 " + (activeCitation ? "xl:w-1/2" : "")}>
                            <div className="flex-1 overflow-hidden">
                                {appStateContext?.state.assistant &&
                                    (!lastQuestionRef.current ? (
                                        <div className="relative h-full">
                                            <div className="h-full overflow-y-auto w-full" ref={chatScrollRef}>
                                                <div className="p-0">
                                                    <div className="flex flex-col pb-[18px]">
                                                        {hasWelcomeMessage && greetingResponse && (
                                                            showGreetingLoader ? (
                                                                <AnswerLoading message="Thinking" />
                                                            ) : (
                                                                <Answer
                                                                    isStreaming={false}
                                                                    answer={greetingResponse}
                                                                    onCitationClicked={() => { }}
                                                                    onThoughtProcessClicked={() => { }}
                                                                    onCopyAnswerClicked={() =>
                                                                        greetingResponse && copyToClipboard(greetingResponse.choices[0].message.content)
                                                                    }
                                                                    showThoughts={false}
                                                                    lastAnswer={false}
                                                                    windowWidth={windowWidth}
                                                                    activeVideo={activeVideos[0] || ""}
                                                                    onShowVideo={citation => onShowVideo(citation, 0)}
                                                                    onCloseVideo={() => onCloseVideo(0)}
                                                                />
                                                            )
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="relative h-full">
                                            <div className="h-full overflow-y-auto w-full" ref={chatScrollRef}>
                                                <div className="p-0">
                                                    <div className="flex flex-col pb-[18px]">
                                                        {hasWelcomeMessage && greetingResponse && (
                                                            <Answer
                                                                isStreaming={false}
                                                                answer={greetingResponse}
                                                                onCitationClicked={() => { }}
                                                                onThoughtProcessClicked={() => { }}
                                                                onCopyAnswerClicked={() =>
                                                                    greetingResponse && copyToClipboard(greetingResponse.choices[0].message.content)
                                                                }
                                                                showThoughts={false}
                                                                lastAnswer={false}
                                                                windowWidth={windowWidth}
                                                                activeVideo={activeVideos[0] || ""}
                                                                onShowVideo={citation => onShowVideo(citation, 0)}
                                                                onCloseVideo={() => onCloseVideo(0)}
                                                            />
                                                        )}
                                                        {isStreaming &&
                                                            streamedAnswers.map((streamedAnswer, index) => (
                                                                <div key={index}>
                                                                    <UserChatMessage message={streamedAnswer[0]} files={questionFiles[index]?.files} />
                                                                    <Answer
                                                                        isStreaming={true}
                                                                        key={index}
                                                                        answer={streamedAnswer[1]}
                                                                        onCitationClicked={(citation, citationTitle) =>
                                                                            onShowCitation(citation, citationTitle)
                                                                        }
                                                                        onThoughtProcessClicked={() => onThoughtProcessClicked(index)}
                                                                        onCopyAnswerClicked={() => copyToClipboard(streamedAnswer[1].choices[0].message.content)}
                                                                        showThoughts={showThoughts[index]}
                                                                        lastAnswer={index === answers.length}
                                                                        windowWidth={windowWidth}
                                                                        activeVideo={activeVideos[hasWelcomeMessage ? index + 1 : index]}
                                                                        onShowVideo={citation => onShowVideo(citation, hasWelcomeMessage ? index + 1 : index)}
                                                                        onCloseVideo={() => onCloseVideo(hasWelcomeMessage ? index + 1 : index)}
                                                                        streamingReasoningText={index === streamedAnswers.length - 1 ? streamingReasoning : undefined}
                                                                        streamingWebSearchStatus={index === streamedAnswers.length - 1 ? streamingWebSearch : undefined}
                                                                        streamingFileGenerationStatus={index === streamedAnswers.length - 1 ? streamingFileGeneration : undefined}
                                                                    />
                                                                </div>
                                                            ))}
                                                        {!isStreaming &&
                                                            answers.map((answer, index) => (
                                                                <div key={index} className={"" + (index === 0 ? "" : "")}>
                                                                    <UserChatMessage message={answer[0]} files={questionFiles[index]?.files} />
                                                                    <Answer
                                                                        isStreaming={false}
                                                                        key={index}
                                                                        answer={answer[1]}
                                                                        onCitationClicked={(citation, citationTitle) =>
                                                                            onShowCitation(citation, citationTitle)
                                                                        }
                                                                        onThoughtProcessClicked={() => onThoughtProcessClicked(index)}
                                                                        onCopyAnswerClicked={() => copyToClipboard(answer[1].choices[0].message.content)}
                                                                        showThoughts={showThoughts[index]}
                                                                        lastAnswer={index === answers.length}
                                                                        windowWidth={windowWidth}
                                                                        activeVideo={activeVideos[hasWelcomeMessage ? index + 1 : index]}
                                                                        onShowVideo={citation => onShowVideo(citation, hasWelcomeMessage ? index + 1 : index)}
                                                                        onCloseVideo={() => onCloseVideo(hasWelcomeMessage ? index + 1 : index)}
                                                                    />
                                                                </div>
                                                            ))}
                                                        {isLoading && (
                                                            <div>
                                                                <UserChatMessage message={lastQuestionRef.current} files={lastQuestionFilesRef.current} />
                                                                <AnswerLoading
                                                                    message={
                                                                        effectiveWebSearch
                                                                            ? "Thinking"
                                                                            : reasoning
                                                                                ? "Thinking"
                                                                                : "Generating answer"
                                                                    }
                                                                />
                                                            </div>
                                                        )}
                                                        {error ? (
                                                            <div className={"" + (answers.length === 0 ? "" : "")}>
                                                                <UserChatMessage message={lastQuestionRef.current} files={lastQuestionFilesRef.current} />
                                                                <AnswerError error={error.toString()} onRetry={() => makeApiRequest(lastQuestionRef.current)} />
                                                            </div>
                                                        ) : null}
                                                        <div ref={chatMessageStreamEnd} />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                            </div>
                            {appStateContext?.state.assistant && (
                                <div className="w-full pt-0">
                                    {isWorldCheck ? (
                                        <FormInput
                                            clearOnSend
                                            disabled={
                                                appStateContext.state.answerLoading ||
                                                appStateContext.state.files.some(file => file.status === "Loading")
                                            }
                                            onSend={question => makeApiRequest(question)}
                                            chatEmpty={!lastQuestionRef.current}
                                            windowWidth={windowWidth}
                                            fileInputRef={fileInputRef}
                                            uploadFile={uploadFile}
                                            showToEnd={!isAutoScroll && lastQuestionRef.current !== ""}
                                            toEnd={scrollToEnd}
                                        />
                                    ) : (
                                        <QuestionInput
                                            ref={questionInputRef}
                                            clearOnSend
                                            disabled={
                                                appStateContext.state.answerLoading ||
                                                appStateContext.state.files.some(file => file.status === "Loading")
                                            }
                                            isGenerating={isLoading || isStreaming}
                                            onStop={stopGenerating}
                                            onSend={question => makeApiRequest(question)}
                                            chatEmpty={!lastQuestionRef.current}
                                            windowWidth={windowWidth}
                                            fileInputRef={fileInputRef}
                                            uploadFile={uploadFile}
                                            showToEnd={!isAutoScroll && lastQuestionRef.current !== ""}
                                            toEnd={scrollToEnd}
                                            reasoning={effectiveReasoning}
                                            setReasoning={setReasoning}
                                            webSearch={effectiveWebSearch}
                                            setWebSearch={setWebSearch}
                                        />
                                    )}
                                </div>
                            )}
                            {appStateContext?.state.assistantsLoadingState === AssistantsLoadingState.Loading && (
                                <div className="relative h-full">
                                    <div className="flex h-full flex-col items-center justify-center">
                                        <a className="pt-24 gap-1 w-fit font-bold flex text-center items-center text-lg whitespace-nowrap">
                                            <span className="flex justify-center items-center h-[26.67px] w-[26.67px]">
                                                <Spinner style={{ display: "block", verticalAlign: "middle" }} styles={spinnerStyles} />
                                            </span>
                                            <span className="text-[0.875rem] text-jtc-white uppercase tracking-[.15rem]">
                                                <span className="">{isScreeningUrl ? "Loading" : "Loading assistants"}</span>
                                            </span>
                                        </a>
                                    </div>
                                </div>
                            )}
                        </div>
                        {activeCitation && (
                            <div className="flex-1 overflow-y-auto ml-6 mb-[36px] hidden w-1/2 xl:flex">
                                <div className="w-full pb-4 px-4 justify-center md:gap-6 m-auto lg:mr-16 bg-jtc-grey rounded-lg">
                                    <div className="relative flex w-full flex-col">
                                        <div className="flex justify-between height-[40px] my-2">
                                            <div className="flex grow justify-start max-w-[calc(100%-4rem)] items-center">
                                                <div className="leading-7 font-semibold text-jtc-purple text-[0.875rem] tracking-[.15rem] text-ellipsis overflow-x-hidden whitespace-nowrap">
                                                    {activeCitationTitle}
                                                </div>
                                            </div>
                                            <div className="flex justify-end">
                                                <ButtonTooltip title="Hide source">
                                                    <span className="">
                                                        <a
                                                            className={
                                                                "flex items-center h-[40px] rounded-lg text-jtc-purple hover:text-jtc-blue cursor-pointer px-[6.5px]"
                                                            }
                                                            onClick={onCloseCitation}
                                                        >
                                                            <span className="shrink-0 text-2xl material-symbols-outlined pt-[3px]">visibility_off</span>
                                                        </a>
                                                    </span>
                                                </ButtonTooltip>
                                            </div>
                                        </div>
                                        {isBrowserRenderable(activeCitation) ? (
                                            <iframe key={iframeKey} title="Citation" src={activeCitation} width="100%" height={windowHeight - 245.25 + "px"} />
                                        ) : (
                                            <div
                                                className="flex flex-col items-center justify-center gap-3 bg-white rounded-lg text-center px-6"
                                                style={{ height: windowHeight - 245.25 + "px" }}
                                            >
                                                <span className="material-symbols-outlined text-5xl text-jtc-purple">draft</span>
                                                <p className="text-jtc-black">
                                                    {citationFileName(activeCitation).split(".").pop()?.toUpperCase()} files can't be displayed in the
                                                    browser.
                                                </p>
                                                <a
                                                    href={activeCitation}
                                                    download={citationFileName(activeCitation)}
                                                    className="flex items-center gap-1 rounded-lg bg-jtc-blue text-jtc-white px-4 py-2 font-semibold hover:opacity-90"
                                                >
                                                    <span className="material-symbols-outlined text-[1.25rem]">download</span>
                                                    Download
                                                </a>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </main>
                </div>
                {windowWidth < 976 && (
                    <MobileSidebar
                        onNewClick={clearChat}
                        newDisabled={(!lastQuestionRef.current && !appStateContext?.state.files.length) || appStateContext?.state.answerLoading}
                        onHistoryClick={handleHistoryClick}
                    />
                )}
            </div>
        </ClearChatProvider>
    );
};

export default Chat;
